import { Card } from '@/components/ui/Card';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { imageUrl } from '@/api/client';
import { cn } from '@/lib/cn';
import { hpFillClass } from '@/lib/hp';
import type { Waifu } from '@/types';

const rarityRing: Record<string, string> = {
  common: 'ring-white/20',
  uncommon: 'ring-success/40',
  rare: 'ring-stardust/60',
  epic: 'ring-accent/70',
  legendary: 'ring-gold/80',
  mythic: 'ring-pink-500/80',
};

interface Props {
  waifu: Waifu;
  onClick?: () => void;
}

export function WaifuCard({ waifu, onClick }: Props) {
  const ring = rarityRing[waifu.rarity] ?? 'ring-white/20';
  const b = waifu.equip_bonuses ?? {};
  const totalMaxHp = waifu.max_hp + (b.hp ?? 0);
  return (
    <Card interactive onClick={onClick} className="p-3 flex gap-3 items-center">
      <div
        className={cn(
          'w-16 h-16 rounded-2xl overflow-hidden shrink-0 ring-2 bg-bg-elevated',
          ring,
        )}
      >
        {waifu.portrait_url ? (
          <img
            src={imageUrl(waifu.portrait_url) ?? ''}
            alt={waifu.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-2xl text-white">
            {waifu.name.charAt(0)}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium text-white truncate">{waifu.name}</div>
            <div className="text-[10px] text-white/50 truncate">
              {waifu.race_name && <>Lv. {waifu.level} · {waifu.race_name}</>}
              {waifu.class_name && <> · {waifu.class_name}</>}
            </div>
          </div>
          <span className="text-[10px] text-white/40 uppercase">{waifu.rarity}</span>
        </div>
        <div className="mt-1.5">
          <ProgressBar
            value={waifu.current_hp}
            max={totalMaxHp}
            color={hpFillClass(waifu.current_hp, totalMaxHp)}
            showLabel
            labelClassName="text-black"
            className="h-4"
          />
        </div>
        <div className="mt-1 flex items-center gap-2 text-[10px] text-white/60">
          <span>
            STR {waifu.strength}
            {(b.strength ?? 0) > 0 && <span className="text-success">+{b.strength}</span>}
          </span>
          <span>
            DEX {waifu.dexterity}
            {(b.dexterity ?? 0) > 0 && <span className="text-success">+{b.dexterity}</span>}
          </span>
          <span>
            INT {waifu.intelligence}
            {(b.intelligence ?? 0) > 0 && <span className="text-success">+{b.intelligence}</span>}
          </span>
          <span className="ml-auto text-pink-300">♥ {waifu.relationship}</span>
        </div>
      </div>
    </Card>
  );
}
